import React from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';

import { colour, pillSx } from '../admin/tokens';

/**
 * How to reach the family, as it stands.
 *
 * The accounts team chase fees, and chasing a fee means ringing somebody — so an address and a
 * mobile are as much theirs as the enrolment team's. What is not theirs is the value the school
 * held before: a parent who corrects their own number is taken at their word, and AR are going to
 * ring the new one regardless.
 *
 * What they do need is to know it is new, so a changed field says "New" and nothing more. Without
 * that, somebody rings the number they already had on file and wonders why nobody answers.
 *
 * Numbers and addresses are links, because the next thing anybody does with them is use them.
 */
const ContactDetails = ({ details = [], title = 'How to contact them' }) => {
    if (!details.length) return null;

    const asLink = (entry) => {
        if (entry.field.startsWith('e_mail')) return `mailto:${entry.value}`;
        if (entry.field.startsWith('mobile')) return `tel:${entry.value.replace(/\s/g, '')}`;
        return null;
    };

    return (
        <Box sx={{ mb: 2.5 }}>
            <Typography sx={{
                fontSize: '12px',
                fontWeight: 700,
                color: colour.body,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                mb: 1.25,
            }}>
                {title}
            </Typography>

            <Box sx={{
                border: `1px solid ${colour.border}`,
                borderRadius: '12px',
                overflow: 'hidden',
            }}>
                {details.map((entry, index) => (
                    <Box
                        key={entry.field}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            px: 1.5,
                            py: 1,
                            borderTop: index ? `1px solid ${colour.border}` : 'none',
                            // A changed field is tinted as well as labelled: somebody scanning for
                            // what is new should not have to read every row to find it.
                            bgcolor: entry.isNew ? colour.primaryTint : 'transparent',
                        }}
                    >
                        <Typography sx={{ fontSize: '12px', color: colour.muted, minWidth: 116 }}>
                            {entry.label}
                        </Typography>

                        {asLink(entry) ? (
                            <Link
                                href={asLink(entry)}
                                sx={{ fontSize: '13.5px', color: colour.primaryDeep, fontWeight: 600 }}
                            >
                                {entry.value}
                            </Link>
                        ) : (
                            <Typography sx={{ fontSize: '13.5px', color: colour.body }}>
                                {entry.value}
                            </Typography>
                        )}

                        {entry.isNew && (
                            <Chip size="small" label="New" sx={{ ...pillSx('good'), ml: 'auto' }} />
                        )}
                    </Box>
                ))}
            </Box>
        </Box>
    );
};

export default ContactDetails;
